import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Plus,
  Upload,
  Loader2,
  Pencil,
  Trash2,
  Save,
  Download,
  FileSpreadsheet,
  X,
  LayoutList,
  Layers,
} from 'lucide-react';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import FlashcardFlipCard from './FlashcardFlipCard';
import {
  fetchFlashcardDecks,
  fetchFlashcards,
  createFlashcard,
  updateFlashcard,
  deleteFlashcard,
  bulkCreateFlashcards,
  type Flashcard,
  type FlashcardDeck,
} from '../../services/flashcardService';
import {
  parseFlashcardCsv,
  downloadFlashcardImportTemplate,
  type ParseFlashcardCsvResult,
} from '../../utils/flashcardCsvUtils';
import type { Tutee } from '../../types/tuition';

const IMPORT_PREVIEW_ROWS = 8;

type DeckFilter = 'all' | 'unassigned' | string;
type CardsViewMode = 'list' | 'flip';

interface FlashcardsMyCardsProps {
  tutee: Tutee;
  studentSlug: string;
  allowManage: boolean;
  onCardsChanged: () => void;
}

function isOwnCard(card: Flashcard, studentSlug: string): boolean {
  return card.studentName?.toLowerCase() === studentSlug.toLowerCase();
}

function deckName(decks: FlashcardDeck[], deckId?: string): string {
  if (!deckId) return 'Unassigned';
  return decks.find((d) => d.id === deckId)?.name ?? 'Deck';
}

function StudentCardRow({
  card,
  editable,
  tutee,
  viewMode,
  onUpdated,
  onDeleted,
}: {
  card: Flashcard;
  editable: boolean;
  tutee: Tutee;
  viewMode: CardsViewMode;
  onUpdated: () => void;
  onDeleted: () => void;
}) {
  const [front, setFront] = useState(card.front);
  const [back, setBack] = useState(card.back);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    setFront(card.front);
    setBack(card.back);
    setError('');
  }, [card.id, card.front, card.back]);

  const dirty = front.trim() !== card.front || back.trim() !== card.back;

  const handleSave = async () => {
    const f = front.trim();
    const b = back.trim();
    if (!f || !b) {
      setError('Both sides are required.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await updateFlashcard(card.id, { front: f, back: b });
      onUpdated();
    } catch (e) {
      console.error(e);
      setError('Could not save changes.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteFlashcard(card.id);
      setConfirmDelete(false);
      onDeleted();
    } catch (e) {
      console.error(e);
      setError('Could not delete card.');
    }
  };

  if (viewMode === 'flip' && !editable) {
    return (
      <li className="min-w-0">
        <FlashcardFlipCard front={card.front} back={card.back} compact />
      </li>
    );
  }

  if (viewMode === 'flip' && editable) {
    return (
      <>
        <li className="p-3 sm:p-4 bg-white border border-indigo-100 rounded-2xl shadow-sm space-y-3 min-w-0">
          <FlashcardFlipCard front={front} back={back} compact />
          <div className="flex items-center gap-2 pt-1 border-t border-gray-100">
            <Pencil className="w-3.5 h-3.5 text-indigo-500 shrink-0" />
            <span className="text-[10px] font-bold uppercase tracking-wide text-indigo-600">Your card</span>
          </div>
          <div className="grid grid-cols-1 gap-2">
            <label className="block">
              <span className="text-[10px] font-bold text-gray-400 uppercase">Front</span>
              <textarea
                value={front}
                onChange={(e) => setFront(e.target.value)}
                rows={2}
                className="mt-1 w-full text-sm border border-gray-200 rounded-xl px-3 py-2 resize-y min-h-[3rem] focus:ring-2 focus:ring-indigo-500/25 outline-none"
              />
            </label>
            <label className="block">
              <span className="text-[10px] font-bold text-gray-400 uppercase">Back</span>
              <textarea
                value={back}
                onChange={(e) => setBack(e.target.value)}
                rows={2}
                className="mt-1 w-full text-sm border border-gray-200 rounded-xl px-3 py-2 resize-y min-h-[3rem] focus:ring-2 focus:ring-indigo-500/25 outline-none"
              />
            </label>
          </div>
          {error && <p className="text-xs text-red-600">{error}</p>}
          <div className="flex flex-wrap gap-2">
            {dirty && (
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold text-white disabled:opacity-50 bg-gradient-to-r ${tutee.colorScheme.gradient}`}
              >
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                Save
              </button>
            )}
            <button
              type="button"
              onClick={() => setConfirmDelete(true)}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold text-red-600 hover:bg-red-50"
            >
              <Trash2 className="w-3.5 h-3.5" />
              Delete
            </button>
          </div>
        </li>
        <ConfirmationModal
          isOpen={confirmDelete}
          onClose={() => setConfirmDelete(false)}
          onConfirm={handleDelete}
          title="Delete your card?"
          message="This cannot be undone."
          confirmText="Delete"
          cancelText="Cancel"
          type="danger"
        />
      </>
    );
  }

  if (!editable) {
    return (
      <li className="p-4 bg-white/90 border border-gray-100 rounded-xl shadow-sm">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
          <div>
            <p className="text-[10px] font-bold text-gray-400 uppercase mb-1">Front</p>
            <p className="text-gray-900 font-medium whitespace-pre-wrap">{card.front}</p>
          </div>
          <div>
            <p className="text-[10px] font-bold text-gray-400 uppercase mb-1">Back</p>
            <p className="text-gray-700 whitespace-pre-wrap">{card.back}</p>
          </div>
        </div>
      </li>
    );
  }

  return (
    <>
      <li className="p-4 bg-white border border-indigo-100 rounded-xl shadow-sm space-y-3">
        <div className="flex items-center gap-2">
          <Pencil className="w-3.5 h-3.5 text-indigo-500" />
          <span className="text-[10px] font-bold uppercase tracking-wide text-indigo-600">Your card</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="block">
            <span className="text-[10px] font-bold text-gray-400 uppercase">Front</span>
            <textarea
              value={front}
              onChange={(e) => setFront(e.target.value)}
              rows={3}
              className="mt-1 w-full text-sm border border-gray-200 rounded-xl px-3 py-2 resize-y min-h-[4rem] focus:ring-2 focus:ring-indigo-500/25 outline-none"
            />
          </label>
          <label className="block">
            <span className="text-[10px] font-bold text-gray-400 uppercase">Back</span>
            <textarea
              value={back}
              onChange={(e) => setBack(e.target.value)}
              rows={3}
              className="mt-1 w-full text-sm border border-gray-200 rounded-xl px-3 py-2 resize-y min-h-[4rem] focus:ring-2 focus:ring-indigo-500/25 outline-none"
            />
          </label>
        </div>
        {error && <p className="text-xs text-red-600">{error}</p>}
        <div className="flex flex-wrap gap-2">
          {dirty && (
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold text-white disabled:opacity-50 bg-gradient-to-r ${tutee.colorScheme.gradient}`}
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              Save
            </button>
          )}
          <button
            type="button"
            onClick={() => setConfirmDelete(true)}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold text-red-600 hover:bg-red-50"
          >
            <Trash2 className="w-3.5 h-3.5" />
            Delete
          </button>
        </div>
      </li>
      <ConfirmationModal
        isOpen={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={handleDelete}
        title="Delete your card?"
        message="This cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
    </>
  );
}

const FlashcardsMyCards = ({ tutee, studentSlug, allowManage, onCardsChanged }: FlashcardsMyCardsProps) => {
  const [cards, setCards] = useState<Flashcard[]>([]);
  const [decks, setDecks] = useState<FlashcardDeck[]>([]);
  const [loading, setLoading] = useState(true);
  const [deckFilter, setDeckFilter] = useState<DeckFilter>('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [addFront, setAddFront] = useState('');
  const [addBack, setAddBack] = useState('');
  const [addDeckId, setAddDeckId] = useState('');
  const [addSaving, setAddSaving] = useState(false);
  const [addError, setAddError] = useState('');
  const [importPreview, setImportPreview] = useState<ParseFlashcardCsvResult | null>(null);
  const [importFileName, setImportFileName] = useState<string | null>(null);
  const [importDeckId, setImportDeckId] = useState('');
  const [importDragActive, setImportDragActive] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [cardsViewMode, setCardsViewMode] = useState<CardsViewMode>('list');
  const importFileInputRef = useRef<HTMLInputElement>(null);

  const allowedDecks = useMemo(
    () => decks.filter((d) => d.isShared || d.createdBy === studentSlug),
    [decks, studentSlug]
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [deckList, cardList] = await Promise.all([
        fetchFlashcardDecks(tutee.id),
        fetchFlashcards(tutee.id, studentSlug),
      ]);
      setDecks(deckList.filter((d) => d.status === 'active'));
      setCards(cardList);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [tutee.id, studentSlug]);

  useEffect(() => {
    load();
  }, [load]);

  const hasUnassigned = cards.some((c) => !c.deckId);

  const filteredCards = useMemo(() => {
    if (deckFilter === 'all') return cards;
    if (deckFilter === 'unassigned') return cards.filter((c) => !c.deckId);
    return cards.filter((c) => c.deckId === deckFilter);
  }, [cards, deckFilter]);

  const groupedSections = useMemo(() => {
    if (deckFilter !== 'all') return null;
    const sections: { key: string; title: string; cards: Flashcard[] }[] = [];
    for (const deck of decks) {
      const deckCards = cards.filter((c) => c.deckId === deck.id);
      if (deckCards.length > 0) sections.push({ key: deck.id, title: deck.name, cards: deckCards });
    }
    const unassigned = cards.filter((c) => !c.deckId);
    if (unassigned.length > 0) {
      sections.push({ key: 'unassigned', title: 'Unassigned', cards: unassigned });
    }
    return sections;
  }, [deckFilter, cards, decks]);

  const deckOptions = [
    { id: '', label: 'My cards (no deck)' },
    ...allowedDecks.map((d) => ({ id: d.id, label: d.name })),
  ];

  const handleAddSave = async () => {
    if (!addFront.trim() || !addBack.trim()) {
      setAddError('Both sides are required.');
      return;
    }
    setAddSaving(true);
    setAddError('');
    try {
      const savedDeckLabel = deckName(allowedDecks, addDeckId || undefined);
      await createFlashcard({
        tuteeId: tutee.id,
        deckId: addDeckId || undefined,
        studentName: studentSlug,
        front: addFront.trim(),
        back: addBack.trim(),
      });
      setShowAddModal(false);
      setAddFront('');
      setAddBack('');
      setAddDeckId('');
      await load();
      onCardsChanged();
      setSuccessMessage(`Card added to ${savedDeckLabel}.`);
    } catch (e) {
      console.error(e);
      setAddError('Could not save card.');
    } finally {
      setAddSaving(false);
    }
  };

  const handleImportFile = (file: File) => {
    if (!file.name.toLowerCase().endsWith('.csv') && file.type !== 'text/csv') return;
    setImportFileName(file.name);
    const reader = new FileReader();
    reader.onload = () => {
      setImportPreview(parseFlashcardCsv(String(reader.result ?? '')));
    };
    reader.readAsText(file);
  };

  const handleBulkImport = async () => {
    if (!importPreview?.rows.length) return;
    setIsImporting(true);
    try {
      const { created } = await bulkCreateFlashcards(
        importPreview.rows.map((row, i) => ({
          tuteeId: tutee.id,
          deckId: importDeckId || null,
          studentName: studentSlug,
          front: row.front,
          back: row.back,
          displayOrder: i,
        }))
      );
      setShowImportModal(false);
      setImportPreview(null);
      setImportFileName(null);
      if (importFileInputRef.current) importFileInputRef.current.value = '';
      await load();
      onCardsChanged();
      setSuccessMessage(`Imported ${created} card${created === 1 ? '' : 's'}.`);
    } catch (e) {
      console.error(e);
    } finally {
      setIsImporting(false);
    }
  };

  const renderCardList = (list: Flashcard[]) => (
    <ul
      className={
        cardsViewMode === 'flip'
          ? 'grid grid-cols-1 sm:grid-cols-2 gap-4'
          : 'space-y-3'
      }
    >
      {list.map((card) => (
        <StudentCardRow
          key={card.id}
          card={card}
          viewMode={cardsViewMode}
          editable={allowManage && isOwnCard(card, studentSlug)}
          tutee={tutee}
          onUpdated={() => {
            load();
            onCardsChanged();
          }}
          onDeleted={() => {
            load();
            onCardsChanged();
          }}
        />
      ))}
    </ul>
  );

  return (
    <div className="animate-fade-in space-y-4">
      {allowManage && (
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => {
              setAddError('');
              setShowAddModal(true);
            }}
            className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold text-white bg-gradient-to-r ${tutee.colorScheme.gradient} shadow-md active:scale-[0.98]`}
          >
            <Plus className="w-4 h-4" />
            Add card
          </button>
          <button
            type="button"
            onClick={() => {
              setImportPreview(null);
              setImportFileName(null);
              setShowImportModal(true);
            }}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold bg-white/80 text-gray-800 border border-gray-200 shadow-sm active:scale-[0.98]"
          >
            <Upload className="w-4 h-4" />
            Import CSV
          </button>
        </div>
      )}

      {!allowManage && (
        <p className="text-sm text-gray-600 bg-white/70 rounded-xl px-4 py-3 border border-white/60">
          Browse cards from your tutor. Personal cards and imports are disabled for your group.
        </p>
      )}

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => setDeckFilter('all')}
          className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all ${
            deckFilter === 'all'
              ? `bg-gradient-to-r ${tutee.colorScheme.gradient} text-white`
              : 'bg-white/80 text-gray-600 border border-gray-200'
          }`}
        >
          All decks
        </button>
        {decks.map((d) => (
          <button
            key={d.id}
            type="button"
            onClick={() => setDeckFilter(d.id)}
            className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all ${
              deckFilter === d.id
                ? `bg-gradient-to-r ${tutee.colorScheme.gradient} text-white`
                : 'bg-white/80 text-gray-600 border border-gray-200'
            }`}
          >
            {d.name}
          </button>
        ))}
        {hasUnassigned && (
          <button
            type="button"
            onClick={() => setDeckFilter('unassigned')}
            className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all ${
              deckFilter === 'unassigned'
                ? `bg-gradient-to-r ${tutee.colorScheme.gradient} text-white`
                : 'bg-white/80 text-gray-600 border border-gray-200'
            }`}
          >
            Unassigned
          </button>
        )}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">View</span>
        <div className="flex gap-1.5 p-1 bg-white/70 rounded-xl border border-gray-200">
          <button
            type="button"
            onClick={() => setCardsViewMode('list')}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
              cardsViewMode === 'list'
                ? `bg-gradient-to-r ${tutee.colorScheme.gradient} text-white shadow-sm`
                : 'text-gray-600 hover:bg-white'
            }`}
          >
            <LayoutList className="w-3.5 h-3.5" />
            Side by side
          </button>
          <button
            type="button"
            onClick={() => setCardsViewMode('flip')}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
              cardsViewMode === 'flip'
                ? `bg-gradient-to-r ${tutee.colorScheme.gradient} text-white shadow-sm`
                : 'text-gray-600 hover:bg-white'
            }`}
          >
            <Layers className="w-3.5 h-3.5" />
            Flip cards
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
        </div>
      ) : cards.length === 0 ? (
        <p className="text-center text-gray-500 py-12">No flashcards yet.</p>
      ) : deckFilter === 'all' && groupedSections ? (
        <div className="space-y-8">
          {groupedSections.map((section) => (
            <section key={section.key}>
              <h3 className="text-sm font-black text-gray-800 uppercase tracking-wide mb-3 flex items-center gap-2">
                {section.title}
                <span className="text-gray-400 font-bold normal-case">({section.cards.length})</span>
              </h3>
              {renderCardList(section.cards)}
            </section>
          ))}
        </div>
      ) : filteredCards.length === 0 ? (
        <p className="text-center text-gray-500 py-12">No cards in this deck.</p>
      ) : (
        renderCardList(filteredCards)
      )}

      {/* Add card modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-black">New flashcard</h3>
              <button type="button" onClick={() => setShowAddModal(false)} className="p-2 rounded-lg hover:bg-gray-100">
                <X className="w-5 h-5" />
              </button>
            </div>
            {addError && <p className="text-sm text-red-600 mb-3">{addError}</p>}
            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Question</label>
            <textarea
              value={addFront}
              onChange={(e) => setAddFront(e.target.value)}
              className="w-full border rounded-xl p-3 mb-3 text-sm min-h-[80px]"
            />
            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Answer</label>
            <textarea
              value={addBack}
              onChange={(e) => setAddBack(e.target.value)}
              className="w-full border rounded-xl p-3 mb-3 text-sm min-h-[80px]"
            />
            {deckOptions.length > 0 && (
              <>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-2">Deck</label>
                <div className="flex flex-wrap gap-2 mb-4">
                  {deckOptions.map((opt) => (
                    <button
                      key={opt.id || 'none'}
                      type="button"
                      onClick={() => setAddDeckId(opt.id)}
                      className={`px-3 py-2 rounded-xl text-sm font-bold border-2 ${
                        addDeckId === opt.id
                          ? 'border-indigo-500 bg-indigo-50 text-indigo-800'
                          : 'border-gray-200'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </>
            )}
            <button
              type="button"
              onClick={handleAddSave}
              disabled={addSaving}
              className={`w-full py-3 rounded-xl font-bold text-white bg-gradient-to-r ${tutee.colorScheme.gradient} disabled:opacity-50 flex items-center justify-center gap-2`}
            >
              {addSaving ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Save card'}
            </button>
          </div>
        </div>
      )}

      {/* Import modal */}
      {showImportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-black">Import your cards</h3>
              <button
                type="button"
                onClick={() => setShowImportModal(false)}
                className="p-2 rounded-lg hover:bg-gray-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <button
              type="button"
              onClick={downloadFlashcardImportTemplate}
              className="mb-4 flex items-center gap-2 text-sm font-bold text-indigo-600"
            >
              <Download className="w-4 h-4" />
              Download template
            </button>
            <input
              ref={importFileInputRef}
              type="file"
              accept=".csv,text/csv"
              className="sr-only"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) handleImportFile(f);
              }}
            />
            {!importPreview ? (
              <div
                role="button"
                tabIndex={0}
                onClick={() => importFileInputRef.current?.click()}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') importFileInputRef.current?.click();
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  setImportDragActive(true);
                }}
                onDragLeave={() => setImportDragActive(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setImportDragActive(false);
                  const f = e.dataTransfer.files?.[0];
                  if (f) handleImportFile(f);
                }}
                className={`rounded-2xl border-2 border-dashed p-8 text-center cursor-pointer mb-4 ${
                  importDragActive ? 'border-indigo-400 bg-indigo-50' : 'border-gray-200 bg-gray-50'
                }`}
              >
                <FileSpreadsheet className="w-10 h-10 text-indigo-500 mx-auto mb-2" />
                <p className="font-bold text-gray-800">Drop CSV or click to browse</p>
              </div>
            ) : (
              <div className="mb-4 space-y-3">
                <p className="text-sm font-bold text-indigo-900">
                  {importFileName} · {importPreview.rows.length} cards ready
                </p>
                <div className="rounded-xl border overflow-hidden max-h-40 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-100 sticky top-0">
                      <tr>
                        <th className="text-left px-2 py-1">Front</th>
                        <th className="text-left px-2 py-1">Back</th>
                      </tr>
                    </thead>
                    <tbody>
                      {importPreview.rows.slice(0, IMPORT_PREVIEW_ROWS).map((row, i) => (
                        <tr key={i} className="border-t">
                          <td className="px-2 py-1">{row.front}</td>
                          <td className="px-2 py-1">{row.back}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            {deckOptions.length > 0 && (
              <>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-2">Add to deck</label>
                <div className="flex flex-wrap gap-2 mb-4">
                  {deckOptions.map((opt) => (
                    <button
                      key={opt.id || 'none'}
                      type="button"
                      onClick={() => setImportDeckId(opt.id)}
                      className={`px-3 py-2 rounded-xl text-sm font-bold border-2 ${
                        importDeckId === opt.id
                          ? 'border-indigo-500 bg-indigo-50 text-indigo-800'
                          : 'border-gray-200'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </>
            )}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setShowImportModal(false)}
                className="flex-1 py-2.5 rounded-xl border font-medium"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={!importPreview?.rows.length || isImporting}
                onClick={handleBulkImport}
                className={`flex-1 py-2.5 rounded-xl text-white font-bold disabled:opacity-50 bg-gradient-to-r ${tutee.colorScheme.gradient}`}
              >
                {isImporting ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Import'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmationModal
        isOpen={!!successMessage}
        onClose={() => setSuccessMessage(null)}
        onConfirm={() => setSuccessMessage(null)}
        title="Success"
        message={successMessage ?? ''}
        confirmText="Done"
        cancelText="Close"
        type="success"
      />
    </div>
  );
};

export default FlashcardsMyCards;
